import { Component } from '@theme/component';

const SWIPE_THRESHOLD = 40;

/** The visitor asked the browser to save data (Chrome's "Lite mode", Android data saver). */
function savesData() {
  const connection = /** @type {{ connection?: { saveData?: boolean } }} */ (navigator).connection;
  return Boolean(connection?.saveData);
}

/**
 * PDP UGC video strip, modelled on the main shop's "Das sagt unsere Community" feed: the active clip sits in the
 * middle and is the largest, its neighbours are medium, the rest small. Only the active clip plays, muted until the
 * visitor turns the sound on. Cards rotate around the active one instead of being cloned for a loop, so each video
 * exists once. Loading starts a viewport before the strip scrolls in: every card gets its poster, the active clip
 * fetches its first seconds, its neighbours do once it plays, the rest load when they become active. Card sizes come
 * from the --ugc-* custom properties in `assets/ugc-videos.css`; this class only positions the cards and drives
 * loading and playback.
 *
 * @typedef {{ track: HTMLElement, cards?: HTMLElement[] }} Refs
 * @extends {Component<Refs>}
 */
class UgcVideosComponent extends Component {
  requiredRefs = ['track'];

  #active = 0;
  /** Shortest signed step of the last change of the active card, used to spot cards that wrap around. */
  #lastStep = 0;
  #inView = false;
  /** Set once the strip is within a viewport of the screen; from then on posters show and clips preload. */
  #nearView = false;
  /**
   * The active clip plays (muted) while in view, like the main shop; pausing it with the card button clears this.
   * With Save-Data it starts cleared, so nothing downloads until the visitor taps a clip.
   */
  #wantsPlay = !savesData();
  /** @type {{ x: number, y: number } | null} */
  #swipeStart = null;
  #swiped = false;

  #intersection = new IntersectionObserver(
    ([entry]) => {
      this.#inView = Boolean(entry?.isIntersecting);
      this.#syncPlayback();
    },
    { threshold: 0.25 }
  );

  #approach = new IntersectionObserver(
    ([entry]) => {
      if (!entry?.isIntersecting) return;
      this.#nearView = true;
      this.#approach.disconnect();
      this.#showPosters();
      this.#preload();
    },
    { rootMargin: '100% 0px' }
  );

  #resize = new ResizeObserver(() => this.#position());

  connectedCallback() {
    super.connectedCallback();

    this.#active = Math.floor((this.#cards.length - 1) / 2);
    this.#position();
    this.#cards.forEach((_, index) => this.#renderSound(index));

    const { track } = this.refs;
    track.addEventListener('pointerdown', this.#onPointerDown);
    track.addEventListener('pointerup', this.#onPointerUp);
    track.addEventListener('pointercancel', this.#onPointerCancel);
    track.addEventListener('play', this.#onMediaChange, true);
    track.addEventListener('pause', this.#onMediaChange, true);
    track.addEventListener('playing', this.#onPlaying, true);
    this.addEventListener('keydown', this.#onKeyDown);
    document.addEventListener('shopify:block:select', this.#onBlockSelect);

    this.#intersection.observe(this);
    this.#approach.observe(this);
    this.#resize.observe(track);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#active = Math.min(this.#active, Math.max(this.#cards.length - 1, 0));
    this.#position();
    this.#showPosters();
    this.#preload();
    this.#syncPlayback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#intersection.disconnect();
    this.#approach.disconnect();
    this.#resize.disconnect();
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
  }

  /**
   * Card button: a side card becomes the active one, the active card toggles play/pause.
   * @param {Event} event
   */
  select(event) {
    if (this.#swiped) return;

    const index = this.#indexOf(event.target);
    if (index === -1) return;

    if (index !== this.#active) {
      this.#activate(index);
      return;
    }

    const video = this.#video(index);
    if (!video) return;
    this.#wantsPlay = video.paused;
    this.#syncPlayback();
  }

  /**
   * Sound button: turns the sound on or off; on a side card it first makes that card the active one.
   * @param {Event} event
   */
  toggleSound(event) {
    const index = this.#indexOf(event.target);
    const video = this.#video(index);
    if (!video) return;

    if (index !== this.#active) this.#activate(index);

    video.muted = !video.muted;
    this.#wantsPlay = true;
    this.#renderSound(index);
    this.#syncPlayback();
  }

  get #cards() {
    return this.refs.cards ?? [];
  }

  /** @param {number} index */
  #video(index) {
    return this.#cards[index]?.querySelector('video') ?? null;
  }

  /** @param {EventTarget | null} target */
  #indexOf(target) {
    return target instanceof Node ? this.#cards.findIndex((card) => card.contains(target)) : -1;
  }

  /** @param {number} index */
  #activate(index) {
    const count = this.#cards.length;
    if (!count || index === this.#active) return;

    const previous = this.#video(this.#active);
    if (previous) {
      previous.pause();
      previous.muted = true;
    }
    this.#renderSound(this.#active);

    let step = (index - this.#active + count) % count;
    if (step > Math.floor(count / 2)) step -= count;
    this.#lastStep = step;

    this.#active = index;
    this.#wantsPlay = true;
    this.#position();
    this.#preload();
    this.#syncPlayback();
  }

  /** @param {number} delta */
  #move(delta) {
    const count = this.#cards.length;
    if (count > 1) this.#activate((this.#active + delta + count) % count);
  }

  /**
   * Places every card relative to the active one: distance 0 in the middle, ±1 next to it, and so on, wrapped so
   * the strip looks endless. Positions are the left edge's offset from the track centre (`--ugc-x`).
   */
  #position() {
    const cards = this.#cards;
    const count = cards.length;
    if (!count) return;

    const style = getComputedStyle(this);
    /** @param {string} name */
    const px = (name) => parseFloat(style.getPropertyValue(name)) || 0;
    const gap = px('--ugc-gap');
    const activeWidth = px('--ugc-w-active');
    /** @param {number} distance */
    const widthAt = (distance) => (distance === 0 ? activeWidth : distance === 1 ? px('--ugc-w-near') : px('--ugc-w'));
    const half = Math.floor(count / 2);

    cards.forEach((card, index) => {
      let distance = (index - this.#active + count) % count;
      if (distance > half) distance -= count;

      let x = -activeWidth / 2;
      if (distance !== 0) {
        const steps = Math.abs(distance);
        let edge = activeWidth / 2 + gap;
        for (let k = 1; k < steps; k++) edge += widthAt(k) + gap;
        x = distance > 0 ? edge : -edge - widthAt(steps);
      }

      const previous = card.dataset.distance;
      const wraps = previous !== undefined && Number(previous) - this.#lastStep !== distance;

      card.dataset.distance = String(distance);
      card.dataset.state = distance === 0 ? 'active' : Math.abs(distance) === 1 ? 'near' : 'far';
      card.style.zIndex = String(count - Math.abs(distance));

      if (wraps) {
        card.classList.add('is-wrapping');
        card.style.setProperty('--ugc-x', `${x}px`);
        card.getBoundingClientRect();
        requestAnimationFrame(() => card.classList.remove('is-wrapping'));
      } else {
        card.style.setProperty('--ugc-x', `${x}px`);
      }
    });

    this.#lastStep = 0;
  }

  /**
   * Posters are rendered as `data-poster` because a `poster` attribute downloads at page start, far below the fold.
   * Once the strip is near the viewport every card gets its poster (~5 small images), also when the visitor saves
   * data, so the cards aren't blank.
   */
  #showPosters() {
    if (!this.#nearView) return;

    this.#cards.forEach((_, index) => {
      const video = this.#video(index);
      if (video?.dataset.poster && !video.poster) video.poster = video.dataset.poster;
    });
  }

  /**
   * Once the strip is near the viewport the active clip switches from `preload="none"` to `metadata`, which makes the
   * browser fetch its first seconds without a `play()` call, so it plays the moment it scrolls in. Once it plays (the
   * strip is on screen), its two neighbours do the same, so a tap or swipe doesn't start from nothing. Clips further
   * away load when they become active. Each clip is a single MP4 sized to its card (480p on phones, 720p from 750px,
   * see `snippets/video-sources.liquid`), so the first seconds are a small, fixed download. `metadata` rather than
   * `auto`: same instant start in Chromium, but a visitor who stops just above the strip doesn't buffer whole clips.
   * Skipped when the visitor saves data.
   */
  #preload() {
    if (!this.#nearView || savesData()) return;

    const count = this.#cards.length;
    const active = this.#video(this.#active);
    const activePlays = !active || (!active.paused && active.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA);

    this.#cards.forEach((_, index) => {
      const video = this.#video(index);
      const offset = Math.abs(index - this.#active);
      const distance = Math.min(offset, count - offset);
      if (video?.preload === 'none' && (distance === 0 || (distance === 1 && activePlays))) video.preload = 'metadata';
    });
  }

  #syncPlayback() {
    this.#cards.forEach((_, index) => {
      const video = this.#video(index);
      if (!video) return;

      if (index === this.#active && this.#inView && this.#wantsPlay) {
        if (video.paused) video.play().catch(() => {});
      } else if (!video.paused) {
        video.pause();
      }
    });
  }

  /** @param {number} index */
  #renderSound(index) {
    const card = this.#cards[index];
    const video = this.#video(index);
    const button = card?.querySelector('.ugc-videos__sound');
    if (!card || !video || !(button instanceof HTMLElement)) return;

    card.classList.toggle('is-unmuted', !video.muted);
    button.setAttribute('aria-label', (video.muted ? button.dataset.labelUnmute : button.dataset.labelMute) ?? '');
  }

  /** @param {Event} event */
  #onMediaChange = (event) => {
    const video = event.target;
    if (!(video instanceof HTMLVideoElement)) return;

    const card = video.closest('.ugc-videos__card');
    const button = card?.querySelector('.ugc-videos__hit');
    if (!card || !(button instanceof HTMLElement)) return;

    const playing = !video.paused;
    card.classList.toggle('is-playing', playing);
    button.setAttribute('aria-label', (playing ? button.dataset.labelPause : button.dataset.labelPlay) ?? '');
  };

  /** @param {Event} event */
  #onPlaying = (event) => {
    if (event.target === this.#video(this.#active)) this.#preload();
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.pointerType === 'mouse' && event.button !== 0) return;
    this.#swipeStart = { x: event.clientX, y: event.clientY };
    this.#swiped = false;
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const start = this.#swipeStart;
    this.#swipeStart = null;
    if (!start) return;

    const dx = event.clientX - start.x;
    const dy = event.clientY - start.y;
    if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) < Math.abs(dy)) return;

    // Swallow the click that follows this pointerup so the swipe doesn't also select a card.
    this.#swiped = true;
    setTimeout(() => {
      this.#swiped = false;
    });
    this.#move(dx < 0 ? 1 : -1);
  };

  #onPointerCancel = () => {
    this.#swipeStart = null;
  };

  /** @param {KeyboardEvent} event */
  #onKeyDown = (event) => {
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    this.#move(event.key === 'ArrowRight' ? 1 : -1);
    const button = this.#cards[this.#active]?.querySelector('.ugc-videos__hit');
    if (button instanceof HTMLElement) button.focus({ preventScroll: true });
  };

  /** @param {Event} event */
  #onBlockSelect = (event) => {
    const index = this.#indexOf(event.target);
    if (index !== -1) this.#activate(index);
  };
}

if (!customElements.get('ugc-videos-component')) {
  customElements.define('ugc-videos-component', UgcVideosComponent);
}
